import { AppLink } from '@/components/links/AppLink';
import { useManifest } from '@/contexts/ManifestContext';
import { Routes } from '@/routes';
import { cn } from '@/utils/cn';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface NavLinkConfig {
  route: string;
  external: boolean;
  translationKey: string;
}

// Green-black, Capital Case text link (DM Sans / body font) with a pill hover
// state and no icons — matches the "Navigation Label" component in the design.
const linkClassName =
  'text-primary-alt font-body rounded-full px-3 py-2 text-sm font-medium transition-colors hover:bg-neutral-lightest';

const NavLink: React.FC<NavLinkConfig> = ({
  route,
  external,
  translationKey,
}) => {
  const { t } = useTranslation();
  const label = t(translationKey);

  if (external) {
    return (
      <a href={route} className={linkClassName}>
        {label}
      </a>
    );
  }

  return (
    <AppLink href={route} className={linkClassName}>
      {label}
    </AppLink>
  );
};

const DesktopNav: React.FC = () => {
  const { features } = useManifest();

  const navLinks: (NavLinkConfig | null)[] = [
    {
      route: Routes.Projects,
      external: false,
      translationKey: 'layout.menu.projects',
    },
    features.showUpdatesNav !== false
      ? {
          // The /impact route is labelled "Updates" in the navigation.
          route: Routes.Impact,
          external: false,
          translationKey: 'layout.menu.impact',
        }
      : null,
    features.showSupportersNav !== false
      ? {
          route: Routes.Supporters,
          external: false,
          translationKey: 'layout.menu.supporters',
        }
      : null,
    features.showAboutNav
      ? {
          route: 'https://resources.sumthing.org/about',
          external: true,
          translationKey: 'layout.menu.about',
        }
      : null,
    features.showBusinessNav
      ? {
          route: Routes.Business,
          external: false,
          translationKey: 'layout.menu.for-business',
        }
      : null,
  ];

  return (
    <nav className={cn('flex items-center')}>
      {navLinks.filter(Boolean).map((link) => (
        <NavLink
          key={link!.route}
          route={link!.route}
          external={link!.external}
          translationKey={link!.translationKey}
        />
      ))}
    </nav>
  );
};

export default DesktopNav;
